/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */

import { captureException } from "@sentry/nextjs";
import { WalletNotConnectedError } from "@solana/wallet-adapter-base";
import type { WalletContextState } from "@solana/wallet-adapter-react";
import {
  type Commitment,
  type Connection,
  type PublicKey,
  type RpcResponseAndContext,
  type Signer,
  type Transaction,
  type TransactionError,
  type VersionedTransaction,
  type SignatureResult,
} from "@solana/web3.js";
import base58 from "bs58";
import i18n from "i18next";

import { addPriorityFeeInstruction } from "@utils/add-priority-fee-instruction";
import { TransactionErrorWithLogs } from "@utils/transaction-error-with-logs";

type TxOptions = {
  confirmationCommitment?: Commitment;
  feePayer?: PublicKey;
  signers?: Signer[];
};

type TxResult = {
  signature: string;
  confirmed: boolean;
  error: TransactionError | null;
  logs?: string[];
};

interface SendAndConfirmTransaction extends SendOptions {
  connection: Connection;
  signedTx: Transaction;
  options?: TxOptions;
}

interface SendOptions {
  minContextSlot: number;
  blockhash: string;
  lastValidBlockHeight: number;
}

const MISSING_SIGNATURE_ERROR_TEXT = i18n.t("appPage.errors.missingSignature");
const SIGN_TRANSACTION_ERROR = i18n.t(
  "appPage.errors.transactionSigningFailed"
);

const COINBASE_WALLET_NAME = "Coinbase Wallet";
const WALLETCONNECT_WALLET_NAME = "WalletConnect";

const handleDetailedError = async (
  connection: Connection,
  result: RpcResponseAndContext<SignatureResult>,
  signature: string
): Promise<TxResult> => {
  if (result.value && result.value.err) {
    const txDetails = await connection.getTransaction(signature, {
      maxSupportedTransactionVersion: 0,
    });

    if (txDetails) {
      throw new TransactionErrorWithLogs(
        txDetails.meta?.err || undefined,
        txDetails.meta?.logMessages || [],
        signature
      );
    } else {
      throw new TransactionErrorWithLogs(result.value.err, [], signature);
    }
  }

  return {
    signature,
    confirmed: !result.value.err,
    error: result.value.err,
  };
};

// const handleSimulateTx = async (
//   connection: Connection,
//   wallet: WalletContextState,
//   unSignedTx: Transaction,
//   signers?: Signer[]
// ) => {
//   if (signers?.length) {
//     unSignedTx.partialSign(...signers);
//   }

//   const signedTx = await wallet.signTransaction?.(unSignedTx);

//   if (!signedTx) {
//     throw new Error(SIGN_TRANSACTION_ERROR);
//   }

//   if (!signedTx.signature) {
//     throw new Error(MISSING_SIGNATURE_ERROR_TEXT);
//   }

//   const result = await connection.simulateTransaction(signedTx);

//   return {
//     signedTx,
//     signature: base58.encode(signedTx.signature),
//     confirmed: !result.value.err,
//     error: result.value.err,
//     logs: result.value?.logs || [],
//   };
// };

// const handleSimulateVtx = async (
//   connection: Connection,
//   wallet: WalletContextState,
//   unSignedTx: VersionedTransaction,
//   signers?: Signer[]
// ) => {
//   if (signers?.length) {
//     unSignedTx.sign(signers);
//   }

//   const signedTx = await wallet.signTransaction?.(unSignedTx);

//   if (!signedTx) {
//     throw new Error(SIGN_TRANSACTION_ERROR);
//   }

//   const encodedSignatures = signedTx.signatures
//     .map((sig) => base58.encode(sig))
//     .join(", ");

//   if (!encodedSignatures) {
//     throw new Error(MISSING_SIGNATURE_ERROR_TEXT);
//   }

//   const result = await connection.simulateTransaction(signedTx);

//   return {
//     signedTx,
//     signature: encodedSignatures,
//     confirmed: !result.value.err,
//     error: result.value.err,
//     logs: result.value?.logs || [],
//   };
// };

const handleSimulateSignedTx = async (
  connection: Connection,
  signedTx: Transaction
) => {
  const result = await connection.simulateTransaction(signedTx);

  if (!signedTx.signature) {
    throw new Error(MISSING_SIGNATURE_ERROR_TEXT);
  }

  return {
    signature: base58.encode(signedTx.signature),
    confirmed: !result.value.err,
    error: result.value.err,
    logs: result.value?.logs || [],
  };
};

export const handleTx = async (
  wallet: WalletContextState,
  tx: Transaction,
  connection: Connection,
  onSigned: (signature: string) => void,
  priorityFeeLamports: number,
  options?: TxOptions
): Promise<TxResult> => {
  if (!(wallet.connected && wallet.publicKey)) {
    throw new WalletNotConnectedError();
  }

  const commitment = options?.confirmationCommitment || "confirmed";

  const {
    context: { slot: minContextSlot },
    value: { blockhash, lastValidBlockHeight },
  } = await connection.getLatestBlockhashAndContext(commitment);
  const unsignedTx = addPriorityFeeInstruction(tx, priorityFeeLamports);
  unsignedTx.recentBlockhash = blockhash;

  if (!unsignedTx.feePayer || options?.feePayer) {
    unsignedTx.feePayer = options?.feePayer ?? wallet.publicKey;
  }

  if (options?.signers?.length) {
    unsignedTx.partialSign(...options.signers);
  }

  const signedTx = await wallet.signTransaction?.(unsignedTx);

  if (!signedTx) {
    throw new Error(SIGN_TRANSACTION_ERROR);
  }

  if (!signedTx.signature) {
    throw new Error(MISSING_SIGNATURE_ERROR_TEXT);
  }
  const signature = await connection.sendRawTransaction(signedTx.serialize(), {
    minContextSlot,
    skipPreflight: true,
    preflightCommitment: commitment,
  });

  if (onSigned) onSigned(signature);

  const result = await connection.confirmTransaction(
    {
      blockhash,
      lastValidBlockHeight,
      signature,
      minContextSlot,
    },
    commitment
  );

  return handleDetailedError(connection, result, signature);
};

export const handleVTx = async (
  wallet: WalletContextState,
  tx: VersionedTransaction,
  connection: Connection,
  onSigned: (signature: string) => void,
  options?: TxOptions
): Promise<TxResult> => {
  if (!(wallet.connected && wallet.publicKey)) {
    throw new WalletNotConnectedError();
  }
  const commitment = options?.confirmationCommitment || "confirmed";
  const {
    context: { slot: minContextSlot },
    value: { blockhash, lastValidBlockHeight },
  } = await connection.getLatestBlockhashAndContext(commitment);
  const unsignedTx = tx;

  if (options?.signers?.length) {
    unsignedTx.sign(options.signers);
  }

  const signedTx = await wallet.signTransaction?.(unsignedTx);

  if (!signedTx) {
    throw new Error(SIGN_TRANSACTION_ERROR);
  }

  const encodedSignatures = signedTx.signatures
    .map((sig) => base58.encode(sig))
    .join(", ");

  if (!encodedSignatures) {
    throw new Error(MISSING_SIGNATURE_ERROR_TEXT);
  }

  const signature = await connection.sendRawTransaction(signedTx.serialize(), {
    minContextSlot,
    skipPreflight: true,
    preflightCommitment: commitment,
  });

  if (onSigned) onSigned(signature);

  const result = await connection.confirmTransaction(
    {
      blockhash,
      lastValidBlockHeight,
      signature,
      minContextSlot,
    },
    commitment
  );

  return handleDetailedError(connection, result, signature);
};

async function sendAndConfirmTransaction({
  connection,
  signedTx,
  minContextSlot,
  blockhash,
  lastValidBlockHeight,
  options,
}: SendAndConfirmTransaction) {
  const commitment = options?.confirmationCommitment || "confirmed";
  const signature = await connection.sendRawTransaction(signedTx.serialize(), {
    minContextSlot,
    skipPreflight: true,
    preflightCommitment: commitment,
  });

  const result = await connection.confirmTransaction(
    {
      blockhash,
      lastValidBlockHeight,
      signature,
      minContextSlot,
    },
    commitment
  );

  return handleDetailedError(connection, result, signature);
}

async function simulateAndSendTransaction({
  connection,
  signedTx,
  minContextSlot,
  blockhash,
  lastValidBlockHeight,
  options,
}: SendAndConfirmTransaction) {
  const {
    error: simulationError,
    signature: simulationSignature,
    logs,
  } = await handleSimulateSignedTx(connection, signedTx);

  if (simulationError) {
    return {
      signature: simulationSignature,
      confirmed: false,
      error: simulationError,
      logs,
    };
  }

  return sendAndConfirmTransaction({
    connection,
    signedTx,
    minContextSlot,
    blockhash,
    lastValidBlockHeight,
    options,
  });
}

export const handleTxs = async (
  wallet: WalletContextState,
  txs: Transaction[],
  connection: Connection,
  onSigned: (signatures: string[]) => void,
  priorityFeeLamports: number,
  options?: TxOptions
) => {
  if (!(wallet.connected && wallet.publicKey && wallet.signAllTransactions)) {
    throw new WalletNotConnectedError();
  }

  if (txs.length === 1) {
    const result = await handleTx(
      wallet,
      txs[0],
      connection,
      (signature) => onSigned([signature]),
      priorityFeeLamports,
      options
    );
    return { success: result.confirmed, results: [result] };
  }

  const {
    context: { slot: minContextSlot },
    value: { blockhash, lastValidBlockHeight },
  } = await connection.getLatestBlockhashAndContext(
    options?.confirmationCommitment || "confirmed"
  );
  const unsignedTxs = txs;
  for (const unsignedTx of unsignedTxs) {
    unsignedTx.recentBlockhash = blockhash;
    unsignedTx.feePayer =
      unsignedTx.feePayer || options?.feePayer || wallet.publicKey;
    if (options?.signers?.length) {
      unsignedTx.partialSign(...options.signers);
    }
  }

  const signedTxs = await wallet.signAllTransactions(unsignedTxs);
  if (onSigned) {
    if (signedTxs.some((tx) => !tx.signature)) {
      throw new Error(MISSING_SIGNATURE_ERROR_TEXT);
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    onSigned(signedTxs.map((tx) => base58.encode(tx.signature!)));
  }

  let success = true;
  const results: TxResult[] = [];

  for (const signedTx of signedTxs) {
    const sendOptions: SendOptions = {
      minContextSlot,
      blockhash,
      lastValidBlockHeight,
    };
    const result =
      wallet.wallet?.adapter.name === COINBASE_WALLET_NAME ||
      wallet.wallet?.adapter.name === WALLETCONNECT_WALLET_NAME
        ? await sendAndConfirmTransaction({
            connection,
            signedTx,
            ...sendOptions,
            options,
          })
        : await simulateAndSendTransaction({
            connection,
            signedTx,
            ...sendOptions,
            options,
          });

    success = success && !result.error;
    results.push(result);
  }

  return { success, results };
};

export const signTxs = async (
  wallet: WalletContextState,
  txs: Transaction[],
  connection: Connection,
  options?: TxOptions
): Promise<{
  txs: Transaction[];
  rpcContext: RpcResponseAndContext<{
    blockhash: string;
    lastValidBlockHeight: number;
  }>;
}> => {
  if (!(wallet.connected && wallet.publicKey && wallet.signAllTransactions)) {
    throw new WalletNotConnectedError();
  }

  const rpcContext = await connection.getLatestBlockhashAndContext(
    options?.confirmationCommitment || "confirmed"
  );
  const unsignedTxs = txs;

  for (const unsignedTx of unsignedTxs) {
    unsignedTx.recentBlockhash = rpcContext.value.blockhash;
    if (!unsignedTx.feePayer || options?.feePayer) {
      unsignedTx.feePayer = options?.feePayer ?? wallet.publicKey;
    }
    if (options?.signers) {
      unsignedTx.partialSign(...options.signers);
    }
  }

  const signedTxs = await wallet.signAllTransactions(unsignedTxs);

  return { txs: signedTxs, rpcContext };
};

export const sendSignedTx = async (
  signedTx: Transaction,
  connection: Connection,
  rpcContext: RpcResponseAndContext<{
    blockhash: string;
    lastValidBlockHeight: number;
  }>,
  options?: TxOptions
): Promise<boolean> => {
  try {
    const commitment = options?.confirmationCommitment || "confirmed";
    const {
      error: simulationError,
      logs,
      signature: simulationSignature,
    } = await handleSimulateSignedTx(connection, signedTx);

    if (simulationError) {
      throw new TransactionErrorWithLogs(
        simulationError,
        logs,
        simulationSignature
      );
    }

    const signature = await connection.sendRawTransaction(
      signedTx.serialize(),
      {
        minContextSlot: rpcContext.context.slot,
        skipPreflight: true,
        preflightCommitment: commitment,
      }
    );

    const { value } = await connection.confirmTransaction(
      {
        blockhash: rpcContext.value.blockhash,
        lastValidBlockHeight: rpcContext.value.lastValidBlockHeight,
        signature,
        minContextSlot: rpcContext.context.slot,
      },
      commitment
    );

    return !value.err;
  } catch (err) {
    captureException(err);
    return false;
  }
};
